/**
 * The one flag that says this document is on its way out, and who is allowed to draw on
 * it while it goes.
 *
 * Every way of leaving the archive is a whole-page navigation — Access's logout URL, a
 * reload back through the login page, a donor's `DELETE` and return to the sign-in form.
 * None of them are instant. Between the decision and the new document there is a stretch
 * of time, usually a moment and occasionally a slow second, in which the old page is
 * still on screen and React is still rendering it.
 *
 * What used to fill that stretch was an error. The request that discovered the session
 * had gone still had to reject, react-query still put it in its error state, and the
 * screen still drew the red card for it — a "Signed out after 90 minutes without use"
 * over a fully drawn dashboard, gone again before it could be read. Nothing was wrong
 * with the message; the trouble is that a flash of red on the way to a login page reads
 * as a fault, and the person who reported it had no way to tell it from one.
 *
 * So leaving is a state the whole app can see rather than a local variable in api.js.
 * The first caller to `beginLeaving` wins — several queries can come back refused at
 * once, and only one navigation can happen — and everything that follows renders the
 * same quiet screen (see LeavingScreen in App.jsx) instead of whatever error it was
 * about to. The reason is carried along because the three ways out want different words.
 *
 * Deliberately not React state: the first thing to know about the lapse is usually a
 * fetch inside api.js, which has no component to set state on, and by the time a
 * navigation is committed there is nothing left worth re-rendering except the notice
 * that it is happening.
 */
import { useSyncExternalStore } from 'react'

/**
 * Cloudflare answers this at the edge in front of the apex; it never reaches the Worker.
 * It ends the Access session for the whole Zero Trust organisation, there being no
 * per-application logout — which is the price of the archive's own idle timeout meaning
 * anything at all. See "Session lifetime" in the README.
 *
 * Requested rather than navigated to, and never a destination — see `accessSignOut` in
 * lib/access.js for why a person must not be left looking at it.
 */
export const ACCESS_LOGOUT = '/cdn-cgi/access/logout'

/**
 * Where every way out lands, at either door.
 *
 * On the apex this is the login page: Access answers a document request for `/` with a
 * 302 to the one-time PIN form once there is no cookie to admit, and returns the browser
 * here afterwards. On the donor hostname it is the sign-in form itself, which is the whole
 * of `/` when nobody is signed in. One path, both doors, and correct on each for its own
 * reasons.
 */
export const SIGN_IN = '/'

/**
 * Why the document is leaving, and where it is going.
 *
 * `idle` is the archive's own ninety minutes closing the session, `expired` is an Access
 * session that has run out its absolute lifetime, `signout` is somebody pressing the
 * button. The reason picks the wording; the destination is carried because the screen
 * offers it as a link, and a navigation that fails — the network dropping between the
 * decision and the login page — would otherwise leave somebody watching a spinner with
 * nothing to press.
 *
 * That link is why the destination is never Access's logout URL, at either door. A tap on
 * it while the navigation it stands in for is still in flight is a second request to
 * wherever it points, and a second request to the logout URL is the Cloudflare error page
 * — see lib/access.js. `SIGN_IN` is answered the same way however many times it is asked.
 */
const REASONS = ['idle', 'expired', 'signout']

/*
 * One frozen object rather than two variables, because useSyncExternalStore compares
 * snapshots by identity: a getter that built a fresh object on every call would re-render
 * for ever.
 */
const NOT_LEAVING = Object.freeze({ reason: null, to: null })
let state = NOT_LEAVING
const watchers = new Set()

export const leavingState = () => state
export const isLeaving = () => state.reason !== null

/**
 * Claim the navigation. True for the first caller, false for everybody after it — so a
 * screenful of queries all refused at the same instant produce one trip to the login
 * page rather than a race between several.
 */
export function beginLeaving(reason, to) {
  if (state.reason !== null) return false
  if (!REASONS.includes(reason)) throw new Error(`Unknown reason for leaving: ${reason}`)
  state = Object.freeze({ reason, to: to ?? SIGN_IN })
  for (const notify of watchers) notify()
  return true
}

/** Test seam. Nothing in the app puts a leaving document back. */
export function resetLeavingForTests() {
  state = NOT_LEAVING
  watchers.clear()
}

const subscribe = (notify) => {
  watchers.add(notify)
  return () => watchers.delete(notify)
}

/**
 * The state, as a hook, so a component re-renders when the app commits to leaving.
 * `{reason: null}` until something does.
 */
export const useLeaving = () => useSyncExternalStore(subscribe, leavingState, leavingState)

/**
 * Run `go` once the browser has had a frame to paint whatever the last render put up.
 *
 * This is the whole trick, and it is not optional. Setting `location` is synchronous and a
 * React re-render is not: a navigation started in the same tick as the state change is
 * committed by the browser before React has painted the screen that state change was for.
 * Measured, rather than reasoned about — with the navigation left where it was, the
 * standing-in screen never appeared at all, and what stayed on view until the login page
 * arrived was the archive, or the error card, exactly as before.
 *
 * Two frames, because the first rAF callback runs before the paint it belongs to and the
 * second runs after it. The timeout is not a belt on that brace but a different case: a
 * hidden window never paints, so rAF never fires, and a document that had committed to
 * leaving would sit there for as long as the tab stayed in the background. Whichever
 * arrives first, `go` runs once.
 */
const PAINT_FALLBACK_MS = 200

function afterPaint(go) {
  let done = false
  const once = () => {
    if (done) return
    done = true
    go()
  }
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => requestAnimationFrame(once))
  }
  setTimeout(once, PAINT_FALLBACK_MS)
}

/**
 * The way out, and the only one anything should use: claim the navigation, let the screen
 * that says so paint, then go.
 *
 * Returns false if something else claimed it first, in which case this caller has nothing
 * to do — one document cannot leave twice.
 */
export function leaveFor(reason, to, go) {
  if (!beginLeaving(reason, to)) return false
  afterPaint(go)
  return true
}
